import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import { initializeApp } from 'firebase/app'
import { getDatabase } from 'firebase/database'
import { getFirestore } from 'firebase/firestore'
import { getAuth, onAuthStateChanged, setPersistence, browserLocalPersistence } from 'firebase/auth'
import mealwaveFirebaseConfig from './firebase/mealwave-config'
import tableTechFirebaseConfig from './firebase/config'
import translations from './translations'

// Quasar Framework imports for Vue 2
import Quasar from 'quasar'
import '@quasar/extras/fontawesome-v6/fontawesome-v6.css'
import 'quasar/dist/quasar.min.css'

// CSS imports
import '@fontsource/cairo/400.css'
import '@fontsource/cairo/600.css'
import '@fontsource/cairo/700.css'
import '@fontsource/cairo/800.css'
import '@fontsource/tajawal'
import './assets/theme.css'
import './assets/main.css'
import '@fortawesome/fontawesome-free/css/all.css'

// Configure Quasar for Vue 2
Vue.use(Quasar, {
  config: {
    brand: {
      primary: '#1A9E9A',
      secondary: '#148f89',
      accent: '#1A9E9A',
      dark: '#181c25',
      positive: '#22c55e',
      negative: '#dc2626',
      info: '#3b82f6',
      warning: '#d97706'
    }
  },
  iconSet: 'fontawesome-v6',
  lang: 'en-us'
})

Vue.config.productionTip = false

// Initialize MealWave Firebase (primary - for auth and MealWave orders)
const mealwaveApp = initializeApp(mealwaveFirebaseConfig)
const mealwaveDatabase = getDatabase(mealwaveApp)
const mealwaveFirestore = getFirestore(mealwaveApp)
const mealwaveAuth = getAuth(mealwaveApp)

// Initialize Table Tech Firebase (secondary - for POS orders)
const tableTechApp = initializeApp(tableTechFirebaseConfig, 'tabletech')
const tableTechDatabase = getDatabase(tableTechApp)

console.log('🔥 [FIREBASE] MealWave Firebase initialized:', mealwaveFirebaseConfig.projectId)
console.log('🔥 [FIREBASE] Table Tech Firebase initialized:', tableTechFirebaseConfig.projectId)

// Set authentication persistence to LOCAL (survives browser restarts)
setPersistence(mealwaveAuth, browserLocalPersistence).catch(error => {
  console.error('Error setting auth persistence:', error)
})

// Create Firebase object with both databases
const firebase = {
  // MealWave (primary)
  database: () => mealwaveDatabase,
  firestore: () => mealwaveFirestore,
  app: () => mealwaveApp,
  auth: () => mealwaveAuth,
  // Table Tech (secondary)
  tableTechDatabase: () => tableTechDatabase,
  tableTechApp: () => tableTechApp
}

// Store Firebase instance globally
Vue.prototype.$firebase = firebase
store.dispatch('setFirebase', firebase)

// Set up auth state listener for MealWave
onAuthStateChanged(mealwaveAuth, async (user) => {
  if (user) {
    // User is signed in - get restaurant ID from custom claims
    const token = await user.getIdToken()
    const idTokenResult = await user.getIdTokenResult()
    const restaurantId = idTokenResult.claims.restaurantId || user.email.split('@')[0]

    store.dispatch('setAuthUser', {
      user,
      token,
      restaurantId
    })
  } else {
    // User is signed out
    store.dispatch('clearAuthUser')
  }
})

// Add translation function globally
Vue.prototype.$t = function(key) {
  const language = this.$store.state.language || 'ar'
  return translations[language]?.[key] || key
}

// Initialize app with localStorage data
store.dispatch('initializeFromLocalStorage')

// Ensure light mode is set by default on document
// This runs AFTER initializeFromLocalStorage to ensure correct state
if (!store.state.darkMode) {
  document.documentElement.classList.remove('dark')
  document.documentElement.classList.add('light')
} else {
  document.documentElement.classList.add('dark')
  document.documentElement.classList.remove('light')
}

new Vue({
  router,
  store,
  render: h => h(App)
}).$mount('#app')
